<script setup>
    import { scrollIntoView } from '@/composables/helpers/client-only/scroll';
    import { preserveUTMQueries } from '@/composables/helpers/links';
    import { storeToRefs } from 'pinia';
    import { useWindowStore } from '@/stores/window';
    const { innerWidth, scrollY } = storeToRefs(useWindowStore());
    const lastScrollY = ref(0);
    const scrollingUp = ref(false);
    const stickyNavActive = useState('stickyNavActive', () => false);
    const filtersRef = ref(null);

    watch(scrollY, (newY) => {
        scrollingUp.value = newY < lastScrollY.value;
        lastScrollY.value = newY;
        // Signal header to use pink background when filter bar is stuck
        if (filtersRef.value) {
            const rect = filtersRef.value.getBoundingClientRect();
            const headerHeight = innerWidth.value >= 1024 ? 116 : 62;
            const stuckThreshold = scrollingUp.value ? headerHeight + 2 : 2;
            stickyNavActive.value = rect.top <= stuckThreshold;
        }
    });

    const props = defineProps({
        expandFiltersByDefault: {
            type: Boolean,
            required: false,
            default: false,
        },
        limit: {
            type: Number,
            required: true,
            default: 12,
        },
        categoryList: {
            type: Array,
            required: false,
            default: null,
        },
    });

    const defaultCategories = [
        { name: 'Session Recaps', slug: 'session-recaps' },
        { name: 'Announcements', slug: 'announcements' },
        { name: 'Attendee Resources', slug: 'attendee-resources' },
        { name: 'Community', slug: 'community' },
        { name: 'Case Studies', slug: 'case-studies' },
    ];

    const categories = [
        { name: 'All', slug: 'all', count: 0 },
        ...(props.categoryList?.length ? props.categoryList : defaultCategories).map((c) => ({
            name: c.name,
            slug: c.slug || c.name.toLowerCase().replace(/\s+/g, '-'),
            count: 0,
        })),
    ];

    const route = useRoute();
    const expandFilters = ref(false);
    const container = ref(null);
    const showSearch = ref(false);
    const activeCategory = ref(route?.query?.category?.toLowerCase() || 'all');
    const activeTags = ref(new Set(route?.query?.tags?.split(',')) || []);
    const q = ref(route?.query?.q || '');
    const currentPage = ref(parseInt(route?.query?.page, 10) || 1);

    const setQueryParams = () => {
        if (typeof window === 'undefined') return;

        const currentParams = new URLSearchParams(window.location.search);

        const params = new URLSearchParams();

        if (activeCategory.value !== 'all') {
            params.set('category', activeCategory.value);
        }
        if (activeTags.value.size) {
            params.set('tags', Array.from(activeTags.value).join(','));
        }
        if (q.value) {
            if (currentParams.get('q') !== q.value) currentPage.value = 1;
            params.set('q', q.value);
        }
        if (currentPage.value !== 1) {
            params.set('page', currentPage.value);
        }
        if (currentParams.get('modal')) {
            params.set('modal', currentParams.get('modal'));
        }
        if (currentParams.get('search')) {
            params.set('search', currentParams.get('search'));
        }
        // Preserve UTM queries and Update URL
        const url = preserveUTMQueries(window.location.pathname);
        if (params.toString() && url.includes('?')) history.replaceState(history.state, '', `${url}&${params.toString()}`);
        else if (params.toString()) history.replaceState(history.state, '', `${url}?${params.toString()}`);
        else if (currentParams.toString() != '') history.replaceState(history.state, '', url);

        if (currentParams.size < params.size) {
            setTimeout(() => {
                scrollToFilters();
            }, 32);
        }
    };

    const handleCategoryClick = (category) => {
        activeCategory.value = category;
        currentPage.value = 1;
    };

    const handleTagClick = (tag) => {
        if (activeTags.value.has(tag)) {
            activeTags.value.delete(tag);
        } else {
            activeTags.value.add(tag);
            currentPage.value = 1;
        }
    };

    const { data: posts, error, pending } = useLazyAsyncData('all-posts', () => $fetch('/api/v1/posts'));

    //set Category counts
    const updateCategoryCounts = () => {
        categories.forEach((category) => {
            if (category.slug === 'all') {
                category.count = posts.value?.length || 0;
                return;
            }
            category.count = posts.value?.filter((post) => post.data.category?.toLowerCase() === category.slug).length || 0;
        });
    };
    watch(posts, updateCategoryCounts, { immediate: true });

    const filteredPosts = computed(() => {
        const filteredPosts =
            posts.value?.filter((post) => {
                if (activeCategory.value !== 'all' && post?.data?.category?.toLowerCase() !== activeCategory.value) {
                    return false;
                }
                if (activeTags.value.size && !post.data?.tags?.some((tag) => activeTags.value.has(tag))) {
                    return false;
                }
                if (activeTags.value.size && !post.data?.tags?.some((tag) => activeTags.value.has(tag))) {
                    return false;
                }
                if (q.value && !post.data.title.toLowerCase().includes(q.value.toLowerCase())) {
                    return false;
                }
                return true;
            }) || [];
        return filteredPosts;
    });

    const totalPages = computed(() => {
        if (!filteredPosts?.value?.length) return 1;
        return Math.ceil(filteredPosts?.value?.length / props.limit);
    });

    watchEffect(() => {
        setQueryParams();
    });

    const initialize = (count = 0) => {
        if (count > 10) return;
        const filters = document.querySelector('.filters');
        if (!filters) return setTimeout(() => initialize(count + 1), 100);
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.toString() !== '') {
            setTimeout(() => {
                scrollToFilters();
            }, 250);
        }
    };

    onMounted(() => {
        initialize();
    });

    // Animation logic
    const postsWrapper = ref();
    const onBeforeLeave = (el) => {
        const wrapperBox = postsWrapper.value.getBoundingClientRect();
        const { width, x, y } = el.getBoundingClientRect();
        el.style.width = `${width}px`;
        el.style.top = `${y - wrapperBox.y}px`;
        el.style.left = `${x - wrapperBox.x}px`;
    };

    const handleCardClick = (payload) => {
        if (payload.type == 'tag') {
            handleTagClick(payload.value);
        }
        if (payload.type == 'category') {
            activeCategory.value = payload.value.toLowerCase();
        }
        scrollToFilters(true);
    };

    const { $bus } = useNuxtApp();

    $bus.on('trigger-blog-card-click', (payload) => {
        handleCardClick(payload);
    });

    const handlePageChange = (page) => {
        currentPage.value = page;
        scrollToFilters(true);
    };

    const handleSearchClick = async () => {
        await navigateTo('/search?search=posts');
    };

    const setFocus = async () => {
        const firstPost = document.querySelector('ul.posts li a');
        firstPost.focus();
    };

    const scrollToFilters = (focus) => {
        if (process.server) return;
        if (!container.value) return;
        scrollIntoView(container.value);
        if (focus) setFocus();
    };

    watch(expandFilters, (newValue) => {
        if (!newValue) setFocus();
    });
</script>

<template>
    <section class="block-blog-filter-grid pb-[4.5rem]" tabindex="-1">
        <div class="px-(--box-padding-lr-m) lg:px-(--box-padding-lr)" ref="container">
            <div ref="filtersRef" class="filters text-(--dark-pink) bg-pink font-hubspot-sans z-[2] py-4">
                <div class="font-normal capitalize flex flex-row items-center gap-4">
                    <ul class="flex justify-start items-center gap-[30px] p-0 pb-0 min-[1299px]:pb-[17px] m-0 max-[1298px]:flex-1 max-[1298px]:min-w-0 max-[1298px]:border-none min-[1299px]:border-b min-[1299px]:border-(--dark-pink)">
                        <li v-for="category in categories" :key="category.slug" class="categories hidden min-[1299px]:flex items-center list-none">
                            <button
                                @click="handleCategoryClick(category.slug)"
                                :aria-label="`Filter the blog for posts with the category ${category.name}`"
                                class="group flex items-center gap-[6px] font-hubspot-serif text-[18px] leading-[1.4] tracking-[0.3px] text-(--magenta)"
                                :class="{ 'blog-category-active': activeCategory === category.slug }">
                                <span
                                    class="group-hover:underline group-hover:decoration-dotted group-hover:decoration-[#a55c87] group-hover:underline-offset-[3px] group-hover:decoration-[1px]"
                                    :class="{ 'underline decoration-dotted decoration-[#a55c87] underline-offset-[3px] decoration-[1px]': activeCategory === category.slug }">
                                    {{ category.name }}
                                </span>
                                <span
                                    class="flex items-center justify-center min-w-[20px] h-[20px] rounded-[100px] px-[4px] font-hubspot-sans text-[10px] leading-none tracking-[0.2px] text-(--magenta)"
                                    :class="activeCategory === category.slug ? 'bg-white' : 'bg-[#eeb4d0] group-hover:bg-white'"
                                    :aria-label="`There are ${category.count} posts in the ${category.name} category. Click to view.`">
                                    {{ category.count }}
                                </span>
                            </button>
                        </li>
                        <li class="select relative m-0 w-full shrink-0 min-[1299px]:hidden flex items-center list-none h-auto">
                            <select
                                v-model="activeCategory"
                                aria-label="Select a category"
                                class="appearance-none w-full relative z-[1] cursor-pointer border border-(--magenta) rounded-[50px] bg-pink text-(--magenta) font-hubspot-sans text-[15px] font-medium tracking-[0.45px] py-[0.9455rem] px-5 pr-12">
                                <option v-for="category in categories" :key="category.slug" :value="category.slug" :selected="activeCategory === category.slug">
                                    {{ category.name }} ({{ category.count }})
                                </option>
                            </select>
                            <MessSvg type="chevron-down" class="absolute right-4 top-1/2 -translate-y-1/2 scale-75 text-[var(--magenta)] pointer-events-none z-[2]" />
                        </li>
                    </ul>
                    <div class="flex items-center gap-4 min-[1024px]:ml-auto">
                        <UButton
                            color="primary"
                            variant="outline"
                            icon="local-filter"
                            @click="expandFilters = !expandFilters"
                            class="more-filters"
                            :ui="{ leadingIcon: 'size-[18px]' }"
                            :aria-expanded="expandFilters"
                            :aria-controls="expandFilters ? 'blog-tags-drawer' : null"
                            aria-label="Show Tag Filters">
                            <span v-if="innerWidth >= 768">Filter By Tag</span>
                            <template v-if="activeTags.size > 0 && innerWidth >= 768"
                                ><span>({{ activeTags.size }})</span></template
                            >
                        </UButton>
                        <ProjectSearchButton @click="handleSearchClick" />
                    </div>
                </div>
                <FragmentsDrawersBlogTags v-model:open="expandFilters" v-model:tags="activeTags" :posts="filteredPosts"></FragmentsDrawersBlogTags>
                <div v-if="activeTags.size" class="bg-pink py-6 flex gap-4 justify-between">
                    <ul class="flex gap-4 flex-wrap">
                        <li v-for="tag in activeTags" :key="tag" class="list-none">
                            <ProjectTag
                                :text="tag"
                                ariaText="Remove Tag"
                                colorSet="white"
                                :useHover="true"
                                :useX="true"
                                @click="handleTagClick(tag)"
                                gtmEventName="blog_filter_tag_clicked" />
                        </li>
                        <li class="list-none">
                            <ProjectTag text="Clear Topics" colorSet="clear-all" :useHover="true" @click="activeTags.clear()" />
                        </li>
                    </ul>
                </div>
            </div>
            <div ref="postsWrapper">
                <span class="sr-only" aria-live="polite">Currently showing {{ filteredPosts.length }} posts.</span>
                <TransitionGroup
                    v-if="filteredPosts.length > 0"
                    name="posts-transition"
                    tag="ul"
                    class="posts flex flex-col gap-10 mt-[40px] mb-0 md:grid md:grid-cols-[repeat(auto-fill,minmax(400px,1fr))] md:gap-[2.6875rem] md:row-gap-[5.5rem] md:mt-[40px] md:mb-0"
                    @before-leave="onBeforeLeave">
                    <li v-for="post in filteredPosts.slice((currentPage - 1) * limit, currentPage * limit)" :key="post.data.url">
                        <article>
                            <FragmentsCardsBlogCard
                                :loading="false"
                                :cardImage="post.data.image"
                                :altText="post.data.imageAltText"
                                :headline="post.data.title"
                                :excerpt="post.data.excerpt"
                                :category="post.data.category"
                                :date="post.data.datePublished"
                                :tags="post.data.tags"
                                size="medium"
                                :link="`${post.data.url}`"
                                @clicked="handleCardClick"
                                :replace="true" />
                        </article>
                    </li>
                </TransitionGroup>
                <div v-else class="text-center my-20 text-(--magenta)">
                    <MessHtml tag="h4" html="No results have been found." class="font-hubspot-sans fs-40 leading-[1.2] mb-2" />
                    <MessHtml tag="p" html="Please adjust your filters and try again." class="font-hubspot-sans fs-16 mt-6 mb-8 leading-7" />
                    <button
                        class="font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] text-(--magenta) border border-(--magenta) py-2.5 px-5 rounded-[50px] transition-all duration-250 hover:bg-(--magenta) hover:text-white"
                        @click="
                            activeCategory = 'all';
                            activeTags.clear();
                            q = '';
                        ">
                        Clear all filters
                    </button>
                </div>
            </div>
            <div class="pagination">
                <ProjectPagination :limit="limit" :totalPages="totalPages" :currentPage="currentPage" @changePage="handlePageChange" :loading="false" />
            </div>
        </div>
    </section>
</template>

<style scoped>
    /* filter button styling */
    :deep(.more-filters) {
        color: var(--magenta);
        border-color: var(--magenta);
        border-width: 1px;
        border-radius: 100px;
        box-shadow: none;
        font-family: var(--font-hubspot-serif);
    }

    :deep(.more-filters:hover) {
        background: var(--magenta);
        color: white;
        border-color: var(--magenta);
    }

    /* [child] — pagination spacing */
    .pagination :deep(.mess-pagination) {
        margin-top: 3.75rem;
    }
    @media (max-width: 768px) {
        .pagination :deep(.mess-pagination) {
            margin-top: 0;
        }
    }

    /* [state] — mobile category list layout */
    @media (max-width: 768px) {
        .block-blog-filter-grid ul li {
            margin: 0;
            height: auto;
        }
    }
</style>
